import { Controller } from '@hotwired/stimulus';
import { csrfToken, localTime } from './helpers/general';
import { default as Inputmask } from 'inputmask';
import UndistractorOptionStore from '../stores/undistractor_option_store';

const hourRegexString = '(^[1-9]$)|(^1[0-2]$)';
const hourRegex = new RegExp(hourRegexString);
const minuteRegexString = '(^[0-5][0-9]$)';
const minuteRegex = new RegExp(minuteRegexString);

export default class extends Controller {
  static ADD_SCHEDULE_URL = '/undistractor_user_schedules/';
  static DEACTIVATE_SCHEDULE_URL = '/undistractor_user_schedules/';

  static values = {
    userScheduleId: Number,
    notSignedIn: Boolean,
    posthogEvents: Object,
  };
  static outlets = ['undistractor-settings-page-row'];
  static targets = [
    'inputStartHour',
    'inputStartMinute',
    'inputStartMeridian',
    'inputEndHour',
    'inputEndMinute',
    'inputEndMeridian',
    'newScheduleView',
    'scheduleOutput',
    'submitButton',
    'deactivateButton',
    'addScheduleButton',
  ];

  connect() {
    this.store = UndistractorOptionStore.getInstance();
    this.#initializeTooltips();
    this.#initializeInputMasks();

    // Only subscribe if the outlet is available
    if (this.hasUndistractorSettingsPageRowOutlet) {
      this.unsubscribe = this.store.subscribe(this.#undistractorOptionId, (option) => {
        if (option && option.schedule !== undefined) {
          this.#updateScheduleOutput(option.schedule);
        }
      });
    }
  }

  disconnect() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.boundDismiss) document.removeEventListener('click', this.boundDismiss);
  }

  clearTextInput(event) {
    event.target.value = '';
  }

  showScheduleForm() {
    // Opening the editor on an existing window must show that window, not empty fields.
    const isNewWindow = !this.#currentSchedule;
    this.#fillScheduleInputs(this.#currentSchedule);
    this.newScheduleViewTarget.hidden = false;
    // Opening the editor for a NEW window slides the pill straight to Schedule (optimistic).
    // #closeEditor drops the class again, sliding it back to Always if cancelled.
    if (isNewWindow) this.element.classList.add('is-scheduling');
    // Dismiss the popover on an outside click. Registered on the next tick so the
    // click that opened it does not immediately close it, and torn down whenever the form closes.
    if (!this.boundDismiss) {
      this.boundDismiss = (event) => {
        if (!this.newScheduleViewTarget.contains(event.target)) this.cancelScheduleForm();
      };
    }
    setTimeout(() => document.addEventListener('click', this.boundDismiss), 0);
  }

  cancelScheduleForm() {
    this.#closeEditor();
  }

  // Any click on the pill flips modes, like the On/Off switch — the two halves are a toggle, not a
  // picker, so it doesn't matter which side is clicked. On the Schedule side (running, or mid new
  // edit) flip back to Always; otherwise flip to Schedule. chooseAlways/chooseSchedule then resolve
  // the exact action for the row's current state.
  toggleMode() {
    const onScheduleSide = this.element.classList.contains('status-scheduled') || this.element.classList.contains('is-scheduling');
    onScheduleSide ? this.chooseAlways() : this.chooseSchedule();
  }

  // The sliding toggle's two buttons dispatch here; each resolves to the right action for the
  // row's current state, so one control covers Always / Scheduled / Always-with-a-window.
  chooseAlways() {
    // Selecting Always also dismisses an open editor — otherwise, mid new-schedule edit the row is
    // still active:true, so the early return below would leave the popup floating with nothing done.
    this.cancelScheduleForm();
    if (!this.hasUndistractorSettingsPageRowOutlet) return;
    if (this.undistractorSettingsPageRowOutlet.undistractorOptionValue.active) return; // already Always
    this.undistractorSettingsPageRowOutlet.activateAlways();
  }

  chooseSchedule() {
    if (!this.hasUndistractorSettingsPageRowOutlet) return this.showScheduleForm();

    const { active, schedule } = this.undistractorSettingsPageRowOutlet.undistractorOptionValue;
    if (!active && schedule) return; // already Scheduled — edit via the window time
    if (active && schedule) return this.undistractorSettingsPageRowOutlet.resumeSchedule(); // remembered -> resume
    this.showScheduleForm(); // fresh -> open the editor for a new window
  }

  deactivateScheduleByEnter(event) {
    if (event.key === 'Enter') {
      this.deactivateSchedule();
    }
  }

  // Optimistic, like the On/Off switch: paint Scheduled and dispatch to the extension now, reconcile
  // with the server in the background, revert on failure.
  addSchedule() {
    this.#addDefaultValues();

    if (!this.#validateInput()) return;

    const oldSchedule = this.#currentSchedule;
    const oldScheduleId = this.userScheduleIdValue;
    const schedule = this.#buildScheduleOutputFromInputs();

    // Paint Scheduled now: the store write dispatches to the extension and updates the sidebar;
    // enterScheduledMode already syncs the active flip optimistically. Only the create is deferred.
    this.#upsertScheduleToStore(schedule);
    this.scheduleOutputTarget.innerText = schedule;
    this.userScheduleIdValue = null; // no server id until the create below lands
    this.#closeEditor();
    if (this.hasUndistractorSettingsPageRowOutlet) this.undistractorSettingsPageRowOutlet.enterScheduledMode();

    if (this.notSignedInValue) {
      this.#captureLitePosthogEvent(this.posthogEventsValue.created);
      return;
    }

    // Background reconcile. The API has no update, so a saved window must be closed first (it 422s
    // otherwise). Kept as a promise so a delete fired before this lands can await the fresh id.
    this.pendingCreate = (async () => {
      let closedExisting = false;
      try {
        if (oldScheduleId) closedExisting = (await this.#deactivateScheduleRequest(oldScheduleId)).ok;
        const response = await this.#remoteScheduleCreateSync();
        if (!response.success) throw new Error('create failed');
        this.userScheduleIdValue = response.id;
        return response.id;
      } catch {
        // Fall back to what the server actually holds: the old window if it survived, else nothing.
        this.#revertToServerTruth(closedExisting ? '' : oldSchedule);
        return null;
      } finally {
        this.pendingCreate = null;
      }
    })();
    this.pendingCreate.catch(() => {}); // revert handled above; just avoid an unhandled rejection
  }

  #revertToServerTruth(surviving) {
    this.#upsertScheduleToStore(surviving);
    // Nothing left to run on a schedule -> back to Always (the pre-schedule mode), not Off.
    if (!surviving && this.hasUndistractorSettingsPageRowOutlet) this.undistractorSettingsPageRowOutlet.activateAlways({ force: true });
    this.#showNetworkErrorTooltip(surviving ? this.deactivateButtonTarget : this.addScheduleButtonTarget);
  }

  // Optimistic delete: drop the window now (dispatches to the extension + updates the sidebar via the
  // store), reconcile with the server in the background, revert on failure.
  deactivateSchedule(event) {
    const thenAlways = !!event?.params?.thenAlways;
    const oldSchedule = this.#currentSchedule;
    const oldScheduleId = this.userScheduleIdValue;
    const pendingCreate = this.pendingCreate;

    this.#clearScheduleInputs();
    this.#upsertScheduleToStore('');
    // ✕ returns to Always; master-off/sidebar just drops the window.
    if (thenAlways && this.hasUndistractorSettingsPageRowOutlet) this.undistractorSettingsPageRowOutlet.activateAlways();

    if (this.notSignedInValue) {
      this.#captureLitePosthogEvent(this.posthogEventsValue.deleted);
      this.userScheduleIdValue = null;
      return;
    }

    // Background: a create may still be in flight — wait for its id before deleting. Revert on failure.
    (async () => {
      try {
        let scheduleId = oldScheduleId;
        if (!scheduleId && pendingCreate) await pendingCreate.catch(() => {});
        scheduleId = scheduleId || this.userScheduleIdValue;
        if (!scheduleId) return; // never persisted (optimistic-only) — nothing to delete

        const response = await this.#deactivateScheduleRequest(scheduleId);
        if (!response.ok) throw new Error('delete failed');
        this.userScheduleIdValue = null;
      } catch {
        this.#upsertScheduleToStore(oldSchedule);
        if (thenAlways && this.hasUndistractorSettingsPageRowOutlet) this.undistractorSettingsPageRowOutlet.resumeSchedule({ force: true });
        this.tooltips.deactivateButton?.show();
      }
    })();
  }

  // Signed-out schedule changes never reach the server, so PostHog capture happens here. Event names
  // arrive from Ruby (PosthogEvents) via the posthog-events data value — lite-only.
  #captureLitePosthogEvent(name) {
    if (name) window.posthog?.capture(name, { ...this.posthogEventsValue.properties, undistractor_option_id: this.#undistractorOptionId });
  }

  #showNetworkErrorTooltip(element) {
    const tooltip = new bootstrap.Tooltip(element, {
      trigger: 'manual',
      placement: 'bottom',
    });
    tooltip.show();
    setTimeout(() => tooltip.dispose(), 5000);
  }

  #deactivateScheduleUrl(scheduleId) {
    return this.constructor.DEACTIVATE_SCHEDULE_URL + scheduleId;
  }

  async #deactivateScheduleRequest(scheduleId) {
    const response = await fetch(this.#deactivateScheduleUrl(scheduleId), {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        undistractor_user_schedules: { local_time: localTime() },
      }),
    });

    return response;
  }

  async #remoteScheduleCreateSync() {
    const scheduleStart = this.#buildStartTime();
    const scheduleEnd = this.#buildEndTime();
    const query = await fetch(this.constructor.ADD_SCHEDULE_URL, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        undistractor_user_schedules: {
          schedule_start: scheduleStart,
          schedule_end: scheduleEnd,
          undistractor_option_id: this.#undistractorOptionId,
          local_time: localTime(),
        },
      }),
    });

    const response = await query.json();

    return response;
  }

  toggleMeridian(event) {
    const keyPress = event.key;

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (keyPress == 'Tab' || keyPress == 'Shift') {
      return;
    }

    event.preventDefault();

    event.target.value = event.target.value == 'PM' ? 'AM' : 'PM';
  }

  hideToolTip(event) {
    this.tooltips[event.target.dataset.scheduleTarget].hide();
  }

  // -----------------------------------------------------------
  // ------------ Private Methods --------------------------- //
  // -----------------------------------------------------------

  #initializeTooltips() {
    const tooltipTriggerList = this.element.querySelectorAll('[data-bs-toggle="tooltip"]');

    this.tooltips = {};
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      this.tooltips[tooltipTriggerEl.dataset.scheduleTarget] = new bootstrap.Tooltip(tooltipTriggerEl);
    });
  }

  // The only resting state is "editor closed" — the pill and window line follow the row's status
  // class, set by the row controller, so there is nothing to swap back to. Leaving by any route also
  // drops the outside-click listener and the optimistic "sliding to Schedule" class, so neither can
  // outlive the editor.
  #closeEditor() {
    this.newScheduleViewTarget.hidden = true;
    if (this.boundDismiss) document.removeEventListener('click', this.boundDismiss);
    this.element.classList.remove('is-scheduling');
  }

  #initializeInputMasks() {
    [this.inputStartHourTarget, this.inputEndHourTarget].forEach((hourInput) => {
      this.#addInputMask(hourInput, hourRegexString, '0');
    });

    [this.inputStartMinuteTarget, this.inputEndMinuteTarget].forEach((minuteInput) => {
      this.#addInputMask(minuteInput, minuteRegexString, '00');
    });
  }

  #addDefaultValues() {
    [this.inputStartMinuteTarget, this.inputEndMinuteTarget].forEach((minuteInput) => {
      if (minuteInput.value.length == 0) {
        minuteInput.value = '0';
      }
    });
  }

  #addInputMask(target, regex, placeholder) {
    Inputmask.default({
      regex: regex,
      placeholder: placeholder,
      clearMaskOnLostFocus: false,
    }).mask(target);
  }

  #validateInput() {
    if (!this.#validateFormat(hourRegex, this.inputStartHourTarget.value, this.tooltips.inputStartHour)) {
      return false;
    }
    if (!this.#validateFormat(minuteRegex, this.inputStartMinuteTarget.value, this.tooltips.inputStartMinute)) {
      return false;
    }
    if (!this.#validateFormat(hourRegex, this.inputEndHourTarget.value, this.tooltips.inputEndHour)) {
      return false;
    }
    if (!this.#validateFormat(minuteRegex, this.inputEndMinuteTarget.value, this.tooltips.inputEndMinute)) {
      return false;
    }

    return true;
  }

  #validateFormat(regex, value, tooltip) {
    if (!regex.test(value)) {
      tooltip.show();
      return false;
    }
    return true;
  }

  #buildStartTime() {
    return this.#buildTime(this.inputStartHourTarget.value, this.inputStartMinuteTarget.value, this.inputStartMeridianTarget.value);
  }

  #buildEndTime() {
    return this.#buildTime(this.inputEndHourTarget.value, this.inputEndMinuteTarget.value, this.inputEndMeridianTarget.value);
  }

  #buildTime(hour, minute, meridian) {
    if (!(hour && minute && meridian)) {
      return null;
    }

    return hour + ':' + minute + meridian;
  }

  #buildScheduleOutput(startTime, endTime) {
    return startTime + '-' + endTime;
  }

  #buildScheduleOutputFromInputs() {
    return this.#buildScheduleOutput(this.#buildStartTime(), this.#buildEndTime());
  }

  // "1:00PM-10:00PM" -> the six inputs. Read from the store rather than server-rendered into the
  // markup, so it stays right after an in-page edit and never bakes one user's window into a
  // cached page.
  #fillScheduleInputs(schedule) {
    const match = /^(\d{1,2}):(\d{2})(AM|PM)-(\d{1,2}):(\d{2})(AM|PM)$/i.exec((schedule || '').trim());
    if (!match) return;

    this.inputStartHourTarget.value = match[1];
    this.inputStartMinuteTarget.value = match[2];
    this.inputStartMeridianTarget.value = match[3].toUpperCase();
    this.inputEndHourTarget.value = match[4];
    this.inputEndMinuteTarget.value = match[5];
    this.inputEndMeridianTarget.value = match[6].toUpperCase();
  }

  #clearScheduleInputs() {
    this.inputStartHourTarget.value = '';
    this.inputEndHourTarget.value = '';
    this.inputStartMinuteTarget.value = '';
    this.inputStartMeridianTarget.value = 'PM';
    this.inputEndMeridianTarget.value = 'PM';
    this.inputEndMinuteTarget.value = '';
  }

  #upsertScheduleToStore(schedule) {
    // Get details from the parent/sibling controller via outlet
    const details = this.undistractorSettingsPageRowOutlet.undistractorOptionValue;

    this.store.upsert({
      ...details,
      schedule: schedule,
    });
  }

  // Keeps the window line's text in step with the store. Whether that line is *shown* is the row
  // status class's job, so a cleared schedule needs no write here — the line is hidden by then.
  #updateScheduleOutput(schedule) {
    if (this.hasScheduleOutputTarget && schedule && schedule.length > 0) this.scheduleOutputTarget.innerText = schedule;
  }

  // Helper getter to access the undistractor option ID from the outlet
  get #undistractorOptionId() {
    return this.undistractorSettingsPageRowOutlet.undistractorOptionValue.id;
  }

  // Helper getter to access the current schedule from the outlet
  get #currentSchedule() {
    if (!this.hasUndistractorSettingsPageRowOutlet) return '';

    return this.undistractorSettingsPageRowOutlet.undistractorOptionValue.schedule || '';
  }
}
